import React, { PureComponent } from "react";
import classnames from "classnames";

import d3 from "~/kits/general/scripts/d3";
import ChartWrapper from "~/core/scripts/chart-components/ChartWrapper";
import XAxis from "~/core/scripts/chart-components/XAxis";
import YAxis from "~/core/scripts/chart-components/YAxis";
import Tooltip from "./Tooltip";
import BulletPoints from "./BulletPoints";
import Swoopy from "./Swoopy";
import { numberFormat } from "~/core/scripts/utils";
import { getCurrentWeek, getData, getDateOfISOWeek } from "../utils";
import { getCurrentData, getDataByWeek, getXTicks } from "./chart-utils";
import { is768Query } from "~/core/scripts/mediaquery-helper";

function getMinMaxWeeks() {
  const currWeek = getCurrentWeek();

  if (currWeek < 7) {
    return [1, currWeek];
  }

  return [currWeek - 6, currWeek];
}

class Chart extends PureComponent {
  state = {
    isLoading: true,
    geojsonData: null,
    isTooltipVisible: false,
    tooltipPos: { x: 0, y: 0 },
    tooltipData: {},
    minDate: false,
    maxDate: false,
    totalCases: 0,
  };

  static defaultProps = {
    selector: "#chart",
    showBulletpoints: true,
    showTooltip: true,
    showLegend: true,
    isTeaser: false,
  };

  constructor(props) {
    super();

    this.container = document.querySelector(props.selector);
    this.containerPaddingLeft = window
      .getComputedStyle(this.container, null)
      .getPropertyValue("padding-left");
    this.width =
      this.container.getBoundingClientRect().width -
      2 * parseInt(this.containerPaddingLeft);
    this.height = props.isTeaser ? 140 : Math.max(this.width * 0.5, 250);
    this.margin = {
      top: 10,
      left: is768Query() && !props.isTeaser ? 46 : 0,
      right: 0,
      bottom: 20,
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData() {
    getData().then((data) => this.initChart(data));
  }

  initChart(data) {
    const currWeek = d3.timeWeek.count(d3.timeYear(new Date()), new Date());
    const yearOfSeasonBegin =
      new Date().getFullYear() - (currWeek < 40 ? 1 : 0); // use previous season until ~october

    const yearOfSeasonEnd = yearOfSeasonBegin + 1;
    const minDate = getDateOfISOWeek(40, yearOfSeasonBegin);
    const maxDate = getDateOfISOWeek(20, yearOfSeasonEnd);

    const dataByWeek = getDataByWeek(data, minDate, maxDate, yearOfSeasonBegin);
    const dataCurrent = getCurrentData(
      data,
      minDate,
      maxDate,
      yearOfSeasonBegin
    );

    const weeks = [...d3.range(40, 53), ...d3.range(1, 21)];
    const dataAll = [
      ...dataByWeek.map((d) => d.absMedian),
      ...data
        .filter(
          (d) =>
            (d.week >= 40 && d.year === yearOfSeasonBegin) ||
            (d.week <= 40 && d.year === yearOfSeasonEnd)
        )
        .map((d) => d.abs),
    ];
    const yMax = d3.max(dataAll);
    const yMaxPlus = yMax * 1.15;
    const yExtent = [0, yMaxPlus];

    const yAxisScale = d3.scaleLinear().domain(yExtent).nice();

    this.x = d3
      .scaleTime()
      .domain([minDate, maxDate])
      .range([this.margin.left, this.width - this.margin.right]);

    this.y = d3
      .scaleLinear()
      .domain(yExtent)
      .range([this.height - this.margin.bottom, this.margin.top])
      .clamp(false);

    this.xAxis = getXTicks(yearOfSeasonBegin, this.props.isTeaser);
    this.yAxis = yAxisScale
      .ticks(5)
      .filter((d) => d !== 0)
      .map((value) => ({ value, label: numberFormat(value) }));

    this.xBandShadow = d3
      .scaleBand()
      .domain(weeks)
      .range([this.margin.left, this.width - this.margin.right]);

    this.xBand = this.xBandShadow.copy().paddingInner(0.2);

    this.line = d3
      .line()
      .x((d) => this.x(d.date))
      .y((d) => this.y(d.absMedian))
      .curve(d3.curveCardinal);

    const totalCases = d3.sum(dataCurrent, (d) => d.abs);

    this.setState({
      isLoading: false,
      data,
      dataByWeek,
      dataCurrent,
      totalCases,
      minDate,
      maxDate,
    });
  }

  getMousePos(evt) {
    const rect = this.chart.svg.getBoundingClientRect();
    const x = evt.clientX - rect.left; //x position within the element.
    const y = evt.clientY - rect.top;

    return { x, y };
  }

  onMouseEnter(evt, data) {
    if (!this.props.showTooltip) {
      return false;
    }
    this.setState({
      isTooltipVisible: true,
      tooltipPos: this.getMousePos(evt),
      tooltipData: data,
    });
  }

  onMouseOut() {
    if (!this.props.showTooltip) {
      return false;
    }
    this.setState({
      isTooltipVisible: false,
      tooltipData: {},
    });
  }

  onMouseMove(evt) {
    if (!this.props.showTooltip) {
      return false;
    }
    this.setState({
      isTooltipVisible: true,
      tooltipPos: this.getMousePos(evt),
    });
  }

  renderBar(d, i) {
    const isActive =
      this.state.tooltipData.week === d.week &&
      this.state.tooltipData.year === d.year;
    const barClasses = classnames(
      "bar",
      { "is-last": i === this.state.dataCurrent.length - 1 },
      { "is-active": isActive }
    );

    return (
      <g
        transform={`translate(${this.xBand(d.week)},0)`}
        key={`bar__${d.week}-${d.year}`}
      >
        <rect
          className={barClasses}
          x={0}
          width={this.xBand.bandwidth()}
          // use 100 as minimum for visability, ca. 2px height
          height={
            this.height - this.margin.bottom - this.y(Math.max(100, d.abs))
          }
          y={this.y(Math.max(100, d.abs))}
        />
        <rect
          className="bar-hover"
          x={0}
          width={this.xBandShadow.bandwidth()}
          height={this.height - this.margin.bottom}
          y={0}
          onMouseEnter={(evt) => this.onMouseEnter(evt, d)}
          onMouseOut={(evt) => this.onMouseOut(evt)}
          onMouseMove={(evt) => this.onMouseMove(evt)}
          onClick={(evt) => this.onMouseEnter(evt, d)}
        />
      </g>
    );
  }
  render() {
    if (this.state.isLoading) {
      return null;
    }

    return (
      <div className="chart-inner">
        {!this.props.isTeaser && (
          <Swoopy
            position={{
              x: this.margin.left + this.x(new Date(2022, 0, 9)),
              y: this.y(1600),
            }}
            isVisible={true}
          />
        )}
        {!this.props.isTeaser && (
          <div className="chart__title">
            Gemeldete Grippe-Fälle in der Saison 2023/2024
          </div>
        )}
        <ChartWrapper
          width={this.width}
          height={this.height}
          ref={(ref) => (this.chart = ref)}
        >
          <XAxis
            width={this.width}
            height={this.height}
            margin={this.margin}
            ticks={this.xAxis}
            drawTickLines
            x={this.x}
            isTeaser={this.props.isTeaser}
          />
          <YAxis
            width={this.width}
            height={this.height}
            margin={this.margin}
            ticks={this.yAxis}
            drawTickLines
            y={this.y}
            isTeaser={this.props.isTeaser}
          />
          <path
            className="chart__line"
            d={this.line(this.state.dataByWeek)}
            stroke="#5050ff"
          />
          {this.state.dataCurrent.map((d, i) => this.renderBar(d, i))}
        </ChartWrapper>
        {this.props.showLegend && (
          <div className="chart__legend">
            <svg width="15" height="15" className="chart__legend-item">
              <path
                className="chart__legend-line"
                d="M 0 15 L 10 10 L 15 0"
              ></path>
            </svg>
            mittlere Fallzahl 2016-2019 (vor Corona)
          </div>
        )}
        {this.props.showTooltip && (
          <Tooltip
            isVisible={this.state.isTooltipVisible}
            data={this.state.tooltipData}
            pos={this.state.tooltipPos}
          />
        )}
        {this.props.showBulletpoints && (
          <BulletPoints
            minDate={this.state.minDate}
            maxDate={this.state.maxDate}
            cases={this.state.totalCases}
          />
        )}
      </div>
    );
  }
}
export default Chart;
